import React from "react";
import LegalShell, { LegalSection } from "./LegalShell";
import { LEGAL } from "../../lib/legalConfig";

/**
 * /legal/aml — AML / KYC disclosure.
 *
 * ⚠️ LAWYER-APPROVED COPY (5 February 2026). The framework is described
 * generically — NO internal thresholds, NO reporting amounts, NO
 * source-of-funds trigger figures. These would tell bad actors how to
 * structure transactions. Do NOT add them.
 */
export default function AmlPage() {
  return (
    <LegalShell
      title="AML / KYC Disclosure"
      effectiveDate={LEGAL.AML_EFFECTIVE_DATE}
      lastUpdated={LEGAL.AML_EFFECTIVE_DATE}
      testid="legal-aml"
      intro={
        <>
          A plain-language summary of the anti-money-laundering and
          identity-verification framework Xchangely operates under.
        </>
      }
    >
      <LegalSection id="commitment" title="Our commitment to a compliant service">
        <p>
          Xchangely operates an anti-money-laundering and
          anti-terrorist-financing (AML/ATF) compliance program in line with
          applicable anti-money-laundering laws and regulations.
        </p>
      </LegalSection>

      <LegalSection id="what-it-means" title="What this means for you">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Identity verification (KYC).</strong> Every customer must
            verify their identity before transacting. We verify identity
            using government-issued identity documents, proof of address,
            and electronic and database checks, including sanctions and
            watchlist screening.
          </li>
          <li>
            <strong>Ongoing monitoring.</strong> We monitor transactions and
            business relationships on a risk basis to detect activity that
            is unusual or inconsistent with what we know about a customer.
          </li>
          <li>
            <strong>Source of funds.</strong> In some circumstances we are
            required to ask you for information or documents about the
            source of your funds. See "Why we ask for Source of Funds."
          </li>
          <li>
            <strong>Sanctions.</strong> We screen customers against
            applicable sanctions and listed-entity sources and comply with
            any directives in force. We do not deal with sanctioned persons
            or prohibited jurisdictions.
          </li>
          <li>
            <strong>Reporting.</strong> Where the law requires, we report
            certain transactions and any suspicious activity to the relevant
            financial-intelligence authorities. We
            are legally prohibited from telling a customer that a
            suspicious-transaction report has been or may be made.
          </li>
          <li>
            <strong>Record-keeping.</strong> We create and retain the
            records the law requires, for at least the period the law
            prescribes.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="what-we-ask" title="What we ask of you">
        <p>
          Provide accurate and complete information, respond to verification
          and source-of-funds requests, and use Xchangely only for lawful
          purposes and for your own account. We may decline, delay, suspend
          or report activity, and freeze funds, where we are required to do
          so by law.
        </p>
      </LegalSection>

      <LegalSection id="questions" title="Questions">
        <p>Compliance-related questions: support@xchangely.io.</p>
      </LegalSection>

      <p className="text-xs text-muted-foreground italic mt-8 border-t border-border pt-4">
        Note: this disclosure describes our program in general terms for
        customers. It is not the compliance program itself and does not
        state internal thresholds or procedures.
      </p>
    </LegalShell>
  );
}
